// ใบคืนหอพักหนึ่งใบ — กรอกผลตรวจ เพิ่มรายการที่ชำรุดนอกแม่บท เซ็นผ่าน หรือยกเลิกใบ
//
// เซ็นผ่าน = คืนหอเรียบร้อย ระบบจะย้ายนักเรียนออกจากหอในทรานแซกชันเดียวกัน ปุ่มจึง
// ถามยืนยันพร้อมบอกยอดค่าเสียหายและรายการที่ไม่ผ่าน — เป็นจุดที่กดแล้วเตียงว่างทันที
import Header from '../layouts/Header';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table,
} from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../services/axiosInterceptor';
import {
  GROUPS, RESULTS, TYPE_LABEL, baht, resultBadge, statusBadge, thaiDate,
} from './clearanceShared';

const emptyNewRow = () => ({ name: '', group: 'condition', result: 'fail', amount: '', note: '' });

const DormitoryClearanceDetail = () => {
  const { dcId } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);
  const [rows, setRows] = useState([]);       // ผลตรวจที่กำลังแก้อยู่บนหน้าจอ
  const [headNote, setHeadNote] = useState('');
  const [newRow, setNewRow] = useState(emptyNewRow);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/api/dormitory/clearance/${dcId}`);
      const data = res.data.data;
      setSlip(data);
      setHeadNote(data.dc_note || '');
      setRows((data.details || []).map((d) => ({
        dcd_id: d.dcd_id,
        item_id: d.dcd_item_id,
        name: d.dcd_name,
        group: d.dcd_group,
        result: d.dcd_result || '',
        amount: Number(d.dcd_amount) || 0,
        note: d.dcd_note || '',
      })));
    } catch (err) {
      setError(err.response?.data?.message || 'อ่านใบคืนหอพักไม่สำเร็จ');
    }
  }, [dcId]);

  useEffect(() => { load(); }, [load]);

  const editable = slip?.dc_status === 'pending';

  const damageTotal = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0), [rows]);
  const failed = useMemo(() => rows.filter((r) => r.result === 'fail'), [rows]);
  const unchecked = useMemo(() => rows.filter((r) => !r.result), [rows]);

  const setRow = (dcdId, patch) =>
    setRows((prev) => prev.map((r) => (r.dcd_id === dcdId ? { ...r, ...patch } : r)));

  // ติ๊กทั้งกลุ่มว่าผ่าน — ของจริงเจ้าหน้าที่เดินตรวจแล้วส่วนใหญ่ผ่าน การกดทีละช่อง
  // สิบกว่าครั้งต่อคนทำให้คนเลิกใช้ใบนี้แล้วกลับไปเซ็นกระดาษเปล่า
  const passAll = () => setRows((prev) => prev.map((r) => (r.result ? r : { ...r, result: 'pass' })));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await axiosInstance.put(`/api/dormitory/clearance/${dcId}/details`, {
        rows: rows.map((r) => ({
          dcd_id: r.dcd_id,
          result: r.result || null,
          amount: Number(r.amount) || 0,
          note: r.note || null,
        })),
        dc_note: headNote,
      });
      setSuccess('บันทึกผลตรวจแล้ว');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกผลตรวจไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const addRow = async (e) => {
    e.preventDefault();
    if (!newRow.name.trim()) return;
    setBusy(true);
    setError('');
    try {
      // ส่งทั้งใบไปพร้อมแถวใหม่ เพื่อไม่ให้ค่าที่พิมพ์ค้างบนหน้าจอหายตอนโหลดใบใหม่
      await axiosInstance.put(`/api/dormitory/clearance/${dcId}/details`, {
        rows: [
          ...rows.map((r) => ({
            dcd_id: r.dcd_id, result: r.result || null,
            amount: Number(r.amount) || 0, note: r.note || null,
          })),
          {
            name: newRow.name.trim(), group: newRow.group, result: newRow.result,
            amount: Number(newRow.amount) || 0, note: newRow.note || null,
          },
        ],
        dc_note: headNote,
      });
      setNewRow(emptyNewRow());
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'เพิ่มรายการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const removeRow = async (dcdId) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/api/dormitory/clearance/${dcId}/details/${dcdId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'ลบรายการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const clearSlip = async () => {
    if (unchecked.length) {
      const go = await Swal.fire({
        icon: 'warning',
        title: `ยังมี ${unchecked.length} รายการที่ไม่ได้ระบุผล`,
        text: 'รายการที่ตั้งไว้ว่าบังคับต้องระบุผลก่อน ระบบจะไม่ให้เซ็นผ่าน',
        showCancelButton: true,
        confirmButtonText: 'ลองเซ็นผ่าน',
        cancelButtonText: 'กลับไปกรอก',
      });
      if (!go.isConfirmed) return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: 'เซ็นผ่านใบคืนหอพัก',
      html: `
        <div class="text-start">
          <div>${slip.st_name} (${slip.st_idnumber})</div>
          <div class="text-muted">${slip.dc_place || '-'}</div>
          <hr class="my-2">
          <div>รายการที่ไม่ผ่าน: <b>${failed.length}</b> รายการ</div>
          <div>ค่าเสียหายรวม: <b>${baht(damageTotal)}</b> บาท</div>
          <hr class="my-2">
          <div class="small">เมื่อเซ็นผ่าน ระบบจะย้ายนักเรียนออกจากหอพัก
          และฝ่ายทะเบียนจะจำหน่ายนักเรียนรายนี้ได้</div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'เซ็นผ่านและย้ายออกจากหอ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#198754',
    });
    if (!confirm.isConfirmed) return;

    setBusy(true);
    setError('');
    try {
      // บันทึกผลที่ค้างบนหน้าจอก่อนเสมอ — ที่เห็นบนจอต้องเป็นสิ่งที่ถูกเซ็น
      await axiosInstance.put(`/api/dormitory/clearance/${dcId}/details`, {
        rows: rows.map((r) => ({
          dcd_id: r.dcd_id, result: r.result || null,
          amount: Number(r.amount) || 0, note: r.note || null,
        })),
        dc_note: headNote,
      });
      const res = await axiosInstance.post(`/api/dormitory/clearance/${dcId}/clear`, {});
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'เซ็นผ่านไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const cancelSlip = async () => {
    const wasCleared = slip.dc_status === 'cleared';
    const ask = await Swal.fire({
      icon: 'warning',
      title: 'ยกเลิกใบคืนหอพัก',
      input: 'text',
      inputLabel: 'เหตุผลที่ยกเลิก (บังคับ)',
      inputPlaceholder: 'เช่น เซ็นผิดคน / นักเรียนไม่ได้ออกจริง',
      text: wasCleared
        ? 'ใบนี้เซ็นผ่านแล้ว ระบบจะพยายามคืนที่พักเดิมให้นักเรียนด้วย'
        : 'ใบที่ยกเลิกจะยังอยู่ในระบบเพื่อให้เลขที่ใบไม่ขาดช่วง',
      showCancelButton: true,
      confirmButtonText: 'ยกเลิกใบนี้',
      cancelButtonText: 'ไม่',
      confirmButtonColor: '#dc3545',
      inputValidator: (v) => (!v || !v.trim() ? 'ต้องระบุเหตุผล' : undefined),
    });
    if (!ask.isConfirmed) return;

    setBusy(true);
    setError('');
    try {
      const res = await axiosInstance.post(`/api/dormitory/clearance/${dcId}/cancel`,
        { reason: ask.value.trim() });
      setSuccess(res.data.message);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'ยกเลิกใบไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  if (!slip) {
    return (
      <React.Fragment>
        <Header />
        <main className="main main-app p-3 p-lg-4">
          <Container fluid className="px-0">
            {error
              ? <Alert variant="danger">{error}</Alert>
              : <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>}
          </Container>
        </main>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Button as={Link} to="/dormitory/clearance" variant="light" size="sm">
              <i className="ri-arrow-left-line me-1" />กลับ
            </Button>
            <h2 className="mb-0">ใบคืนหอพักเลขที่ {slip.dc_no}</h2>
            {statusBadge(slip.dc_status)}
            <div className="ms-auto d-flex gap-2">
              <Button variant="outline-secondary" size="sm"
                onClick={() => navigate(`/dormitory/clearance/${dcId}/print`)}>
                <i className="ri-printer-line me-1" />พิมพ์ใบสำหรับเซ็น
              </Button>
              {slip.dc_status !== 'cancelled' && (
                <Button variant="outline-danger" size="sm" disabled={busy} onClick={cancelSlip}>
                  ยกเลิกใบ
                </Button>
              )}
            </div>
          </div>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          <Row className="g-3 mb-3">
            <Col lg={8}>
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="fs-5 fw-semibold">{slip.st_name}</div>
                      <div className="text-muted">เลขประจำตัว {slip.st_idnumber}</div>
                      <div className="mt-2">
                        <strong>ชั้น:</strong> {slip.dc_class || '-'}
                        {slip.dc_year ? ` (${slip.dc_year})` : ''}
                      </div>
                      <div><strong>หอพัก:</strong> {slip.dc_place || '-'}</div>
                    </Col>
                    <Col md={6}>
                      <div><strong>ประเภท:</strong> {TYPE_LABEL[slip.dc_type] || slip.dc_type}</div>
                      <div><strong>สาเหตุ:</strong> {slip.dc_reason || '-'}</div>
                      <div><strong>วันที่คืนหอ:</strong> {thaiDate(slip.dc_date)}</div>
                      <div><strong>ผู้เปิดใบ:</strong> {slip.dc_by_name || '-'}</div>
                      {slip.dc_status === 'cleared' && (
                        <div className="text-success">
                          <strong>เซ็นผ่านโดย:</strong> {slip.dc_cleared_by_name || '-'}
                        </div>
                      )}
                      {slip.dc_status === 'cancelled' && (
                        <div className="text-danger">
                          <strong>เหตุผลที่ยกเลิก:</strong> {slip.dc_cancel_reason || '-'}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="h-100">
                <Card.Body className="d-flex flex-column justify-content-center">
                  <div className="text-muted">ค่าเสียหาย / ค้างชำระรวม</div>
                  <div className={`fs-3 fw-bold ${damageTotal > 0 ? 'text-danger' : ''}`}>
                    {baht(damageTotal)} บาท
                  </div>
                  <div className="text-muted small">
                    ไม่ผ่าน {failed.length} รายการ · ยังไม่ตรวจ {unchecked.length} รายการ
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-0">ผลการตรวจ</h5>
                {editable && (
                  <Button variant="outline-success" size="sm" className="ms-auto" onClick={passAll}>
                    ติ๊กรายการที่ยังว่างว่า "ผ่าน" ทั้งหมด
                  </Button>
                )}
              </div>

              {GROUPS.map((g) => {
                const list = rows.filter((r) => r.group === g.value);
                if (!list.length) return null;
                return (
                  <div key={g.value} className="mb-4">
                    <div className="fw-semibold mb-2">{g.label}</div>
                    <Table responsive bordered size="sm" className="mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: 220 }}>รายการ</th>
                          <th style={{ width: 240 }}>ผลตรวจ</th>
                          <th style={{ width: 140 }}>ค่าเสียหาย (บาท)</th>
                          <th>หมายเหตุ</th>
                          {editable && <th style={{ width: 48 }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((r) => (
                          <tr key={r.dcd_id}>
                            <td>
                              {r.name}
                              {!r.item_id && <Badge bg="info" className="ms-2">เพิ่มเอง</Badge>}
                            </td>
                            <td>
                              {editable ? (
                                <div className="d-flex gap-3">
                                  {RESULTS.map((opt) => (
                                    <Form.Check
                                      key={opt.value}
                                      type="radio"
                                      id={`r-${r.dcd_id}-${opt.value}`}
                                      name={`result-${r.dcd_id}`}
                                      label={opt.label}
                                      checked={r.result === opt.value}
                                      onChange={() => setRow(r.dcd_id, { result: opt.value })}
                                    />
                                  ))}
                                </div>
                              ) : resultBadge(r.result)}
                            </td>
                            <td>
                              {editable ? (
                                <Form.Control type="number" min="0" step="0.01" size="sm"
                                  value={r.amount}
                                  onChange={(e) => setRow(r.dcd_id, { amount: e.target.value })} />
                              ) : (Number(r.amount) > 0 ? baht(r.amount) : '-')}
                            </td>
                            <td>
                              {editable ? (
                                <Form.Control size="sm" value={r.note}
                                  onChange={(e) => setRow(r.dcd_id, { note: e.target.value })} />
                              ) : (r.note || '-')}
                            </td>
                            {editable && (
                              <td className="text-center">
                                {/* ลบได้เฉพาะรายการที่เพิ่มเอง — รายการจากแม่บทต้องคงอยู่บนใบ
                                    เพื่อให้เห็นว่าทุกอย่างที่ต้องตรวจ ผลเป็นอะไร */}
                                {!r.item_id && (
                                  <Button variant="link" className="text-danger p-0"
                                    disabled={busy} onClick={() => removeRow(r.dcd_id)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                );
              })}

              <Form.Group className="mb-3">
                <Form.Label>หมายเหตุท้ายใบ</Form.Label>
                <Form.Control as="textarea" rows={2} value={headNote} disabled={!editable}
                  onChange={(e) => setHeadNote(e.target.value)} />
              </Form.Group>

              {editable && (
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-primary" disabled={busy} onClick={save}>
                    <i className="ri-save-line me-1" />บันทึกผลตรวจ
                  </Button>
                  <Button variant="success" disabled={busy} onClick={clearSlip}>
                    <i className="ri-check-double-line me-1" />เซ็นผ่าน / คืนหอเรียบร้อย
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          {editable && (
            <Card>
              <Card.Body>
                <h5 className="mb-3">เพิ่มรายการที่ชำรุด/สูญหายนอกรายการตรวจ</h5>
                <Form onSubmit={addRow}>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Control placeholder="เช่น กระจกหน้าต่างแตก" value={newRow.name}
                        onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} />
                    </Col>
                    <Col md={2}>
                      <Form.Select value={newRow.group}
                        onChange={(e) => setNewRow({ ...newRow, group: e.target.value })}>
                        {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={newRow.result}
                        onChange={(e) => setNewRow({ ...newRow, result: e.target.value })}>
                        {RESULTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Control type="number" min="0" step="0.01" placeholder="ค่าเสียหาย"
                        value={newRow.amount}
                        onChange={(e) => setNewRow({ ...newRow, amount: e.target.value })} />
                    </Col>
                    <Col md={2}>
                      <Button type="submit" variant="outline-primary" className="w-100" disabled={busy}>
                        เพิ่ม
                      </Button>
                    </Col>
                    <Col md={12}>
                      <Form.Control placeholder="หมายเหตุ" value={newRow.note}
                        onChange={(e) => setNewRow({ ...newRow, note: e.target.value })} />
                    </Col>
                  </Row>
                </Form>
                <div className="text-muted small mt-2">
                  รายการที่เพิ่มที่นี่อยู่เฉพาะในใบนี้ ถ้าเป็นของที่ต้องตรวจทุกคน
                  ให้เพิ่มที่แท็บ "รายการที่ต้องตรวจ" ของหน้าคืนหอพักแทน
                </div>
              </Card.Body>
            </Card>
          )}

          {!editable && (
            <Alert variant="secondary">
              ใบนี้{slip.dc_status === 'cleared' ? 'เซ็นผ่านแล้ว' : 'ถูกยกเลิกแล้ว'} —
              แก้ผลตรวจไม่ได้ เพราะเป็นเอกสารที่ลงชื่อไปแล้ว
              {slip.dc_status === 'cleared' && ' ถ้าเซ็นผิดให้กด "ยกเลิกใบ" แล้วเปิดใบใหม่'}
              {' '}(กลุ่มรายการด้านบนคือผลที่บันทึกไว้ ณ วันที่เซ็น)
            </Alert>
          )}
        </Container>
      </main>
    </React.Fragment>
  );
};

export default DormitoryClearanceDetail;
